import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { BrandModal } from "./BrandModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export function ChipListModal({
  open,
  onOpenChange,
  title,
  description,
  inputLabel,
  listLabel,
  emptyLabel,
  items,
  onSave,
  theme = "blue",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  inputLabel: string;
  listLabel: string;
  emptyLabel: string;
  items: string[];
  onSave: (items: string[]) => void;
  theme?: "blue" | "emerald";
}) {
  const isEmerald = theme === "emerald";
  const [list, setList] = useState<string[]>([]);
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open) setList(items);
  }, [open, items]);

  function add() {
    const v = value.trim();
    if (!v || list.includes(v)) return;
    setList((l) => [...l, v]);
    setValue("");
  }

  return (
    <BrandModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      theme={theme}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            className={cn(
              isEmerald && "bg-emerald-600 text-white hover:bg-emerald-700",
            )}
            onClick={() => {
              onSave(list);
              onOpenChange(false);
            }}
          >
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold">{inputLabel}</Label>
        <div className="flex gap-3">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="flex-1"
          />
          <Button
            variant="secondary"
            className={cn(
              isEmerald &&
                "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300",
            )}
            onClick={add}
            disabled={!value.trim()}
          >
            + Agregar
          </Button>
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-bold">{listLabel}</p>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {list.map((k) => (
              <span
                key={k}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium",
                  isEmerald
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : "bg-accent text-primary",
                )}
              >
                {k}
                <button
                  onClick={() => setList((l) => l.filter((x) => x !== k))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="size-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </BrandModal>
  );
}
